/**
 * Nuovo test di misura e modifica (V71), nello stesso modale, come le schede.
 *
 * Il nuovo si apre dalla libreria (V70) e, salvato, porta al suo dettaglio;
 * la modifica si apre dal dettaglio (V72) e lo ricarica. In alto nome,
 * istruzioni e RPE e minuti di riferimento, facoltativi; sotto le misure, una
 * card ciascuna: nome e unità, attributi per famiglia, metodo di calcolo con i
 * suoi parametri e l'anteprima di un risultato di prova, con le stesse
 * funzioni del server. Un attributo sta in una misura sola: nelle altre è
 * spento, con il nome della misura che lo ha. La prima misura porta il nome
 * del test finché non la si rinomina.
 */

import { type FormEvent, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  creaTestSchema,
  ETICHETTA_FAMIGLIA_ATTRIBUTO,
  ETICHETTA_METODO_TEST,
  FAMIGLIE_ATTRIBUTO,
  METODI_TEST,
  type MetodoTest,
} from '@calcio/shared';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  OctagonXIcon,
  PlusIcon,
  Trash2Icon,
  XIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { cn } from '@/lib/utils';
import { MenuAzioni, PulsanteInvio } from '../comuni/Azioni.js';
import { chiama } from '../api/client.js';
import { messaggio, useDati } from '../comuni/dati.js';
import { IconaFamiglia } from '../comuni/famiglie.js';
import { StatoCaricamento } from '../comuni/Testata.js';
import { CampoNumero } from './CampoNumero.js';
import { IconaMetodo } from './icone.js';
import {
  anteprima,
  attributiAltrove,
  CAMPI_PARAMETRI,
  CAMPI_RISULTATO,
  corpoDelTest,
  DESCRIZIONE_METODO,
  erroriDelTest,
  formDaTest,
  nomeMisura,
  nuovaMisura,
  spostaMisura,
  unitaDelCampo,
  type AttributoDelTest,
  type FormMisura,
  type FormParametri,
  type FormTest,
  type TestDettaglio,
} from './test-misura.js';

/** Quante misure al massimo, come `creaTestSchema`. */
const MASSIMO_MISURE = 10;

export function DialogTest({
  ss,
  aperto,
  onChiudi,
  esistente,
  onSalvato,
}: {
  ss: string;
  aperto: boolean;
  onChiudi: () => void;
  /** Il test da modificare. */
  esistente?: TestDettaglio;
  /** Dopo la modifica; il nuovo porta invece al suo dettaglio. */
  onSalvato?: () => Promise<void> | void;
}) {
  return (
    <Dialog open={aperto} onOpenChange={(a) => !a && onChiudi()}>
      {/* I campi scorrono; intestazione e piede coi pulsanti restano fermi. */}
      <DialogContent className="flex max-h-[90dvh] flex-col sm:max-w-2xl">
        {aperto && (
          <Caricamento ss={ss} esistente={esistente} onChiudi={onChiudi} onSalvato={onSalvato} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function Caricamento({
  ss,
  esistente,
  onChiudi,
  onSalvato,
}: {
  ss: string;
  esistente?: TestDettaglio;
  onChiudi: () => void;
  onSalvato?: () => Promise<void> | void;
}) {
  const opzioni = useDati<{ attributi: AttributoDelTest[] }>('/test/opzioni');
  if (!opzioni.dati) {
    return (
      <>
        <Intestazione esistente={esistente} />
        <StatoCaricamento errore={opzioni.errore} />
      </>
    );
  }
  return (
    <Form
      ss={ss}
      attributi={opzioni.dati.attributi}
      esistente={esistente}
      onChiudi={onChiudi}
      onSalvato={onSalvato}
    />
  );
}

function Intestazione({ esistente }: { esistente?: TestDettaglio }) {
  return (
    <DialogHeader>
      <DialogTitle>{esistente ? 'Modifica test' : 'Nuovo test'}</DialogTitle>
      <DialogDescription>
        {esistente
          ? `${esistente.nome}: le esecuzioni già fatte tengono le misure di allora.`
          : 'Un esercizio con una o più misure: ognuna dà un punteggio da 1 a 100 ai suoi attributi.'}
      </DialogDescription>
    </DialogHeader>
  );
}

const testVuoto = (): FormTest => ({
  nome: '',
  istruzioni: '',
  rpe: '',
  minuti: '',
  misure: [nuovaMisura()],
});

function Form({
  ss,
  attributi,
  esistente,
  onChiudi,
  onSalvato,
}: {
  ss: string;
  attributi: AttributoDelTest[];
  esistente?: TestDettaglio;
  onChiudi: () => void;
  onSalvato?: () => Promise<void> | void;
}) {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormTest>(() =>
    esistente ? formDaTest(esistente) : testVuoto(),
  );
  const [errori, setErrori] = useState<Record<string, string>>({});
  const [erroreServer, setErroreServer] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  // Gli attivi, più i disattivati che il test valuta già: restano finché non li si toglie.
  const proponibili = useMemo(() => {
    const gia = new Set(esistente?.misure.flatMap((m) => m.attributi.map((a) => a.id)));
    return attributi.filter((a) => a.attivo || gia.has(a.id));
  }, [attributi, esistente]);

  const campo = (c: Partial<Omit<FormTest, 'misure'>>) => setForm((f) => ({ ...f, ...c }));
  const cambiaMisura = (i: number, m: Partial<FormMisura>) =>
    setForm((f) => ({ ...f, misure: f.misure.map((x, j) => (j === i ? { ...x, ...m } : x)) }));
  const misure = (fai: (m: FormMisura[]) => FormMisura[]) =>
    setForm((f) => ({ ...f, misure: fai(f.misure) }));

  const invia = async (e: FormEvent) => {
    e.preventDefault();
    const esito = creaTestSchema.safeParse(corpoDelTest(form));
    if (!esito.success) {
      setErrori(erroriDelTest(esito.error));
      setErroreServer(null);
      return;
    }
    setErrori({});
    setErroreServer(null);
    setInCorso(true);
    try {
      const { id } = await chiama<{ id: string }>(esistente ? `/test/${esistente.id}` : '/test', {
        metodo: esistente ? 'PUT' : 'POST',
        corpo: esito.data,
      });
      toast.success(esistente ? 'Test salvato.' : 'Test creato.');
      if (esistente) {
        await onSalvato?.();
        onChiudi();
      } else {
        onChiudi();
        navigate(`/sq/${ss}/test/${id}`);
      }
    } catch (errore) {
      setErroreServer(messaggio(errore));
      setInCorso(false);
    }
  };

  const qualcheErrore = Object.keys(errori).length > 0;
  const composto = form.misure.length > 1;

  return (
    <form onSubmit={(e) => void invia(e)} className="flex min-h-0 flex-1 flex-col gap-4">
      <Intestazione esistente={esistente} />

      <div className="-mx-4 flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-4 pb-1">
        <FieldGroup>
          <Field data-invalid={Boolean(errori.nome)}>
            <FieldLabel htmlFor="test-nome">Nome</FieldLabel>
            <Input
              id="test-nome"
              value={form.nome}
              placeholder="Sprint 30 m"
              aria-invalid={Boolean(errori.nome)}
              onChange={(e) => campo({ nome: e.target.value })}
            />
            <FieldError>{errori.nome}</FieldError>
          </Field>
          <Field data-invalid={Boolean(errori.istruzioni)}>
            <FieldLabel htmlFor="test-istruzioni">Istruzioni</FieldLabel>
            <Textarea
              id="test-istruzioni"
              value={form.istruzioni}
              placeholder="Come si svolge: partenza, misura, tentativi."
              aria-invalid={Boolean(errori.istruzioni)}
              onChange={(e) => campo({ istruzioni: e.target.value })}
            />
            <FieldError>{errori.istruzioni}</FieldError>
          </Field>
          <FieldSet>
            <FieldLegend variant="label">Carico di riferimento</FieldLegend>
            <FieldDescription>
              Servono per metterlo in seduta come blocco. Un test senza carico, come peso o massa
              grassa, li lascia vuoti.
            </FieldDescription>
            <div className="grid grid-cols-2 gap-5 sm:max-w-sm">
              <CampoNumero
                id="test-rpe"
                etichetta="RPE"
                unita={null}
                valore={form.rpe}
                onCambia={(v) => campo({ rpe: v })}
                errore={errori.rpeRiferimento}
              />
              <CampoNumero
                id="test-minuti"
                etichetta="Minuti"
                unita="min"
                valore={form.minuti}
                onCambia={(v) => campo({ minuti: v })}
                errore={errori.minutiRiferimento}
              />
            </div>
          </FieldSet>
        </FieldGroup>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <h3 className="text-base font-medium">Misure</h3>
            <FieldDescription>
              Ogni misura dà un punteggio da 1 a 100 agli attributi che valuta, uguale per ciascuno:
              lo sprint 100 m può misurare il parziale ai 10 m e il tempo finale.
            </FieldDescription>
          </div>
          {form.misure.map((m, i) => (
            <CardMisura
              key={m.chiave}
              misura={m}
              nome={nomeMisura(form, i)}
              indice={composto ? i : null}
              ultima={i === form.misure.length - 1}
              attributi={proponibili}
              altrove={attributiAltrove(form, i)}
              errori={errori}
              onCambia={(c) => cambiaMisura(i, c)}
              onSposta={(delta) => misure((l) => spostaMisura(l, i, delta))}
              onElimina={composto ? () => misure((l) => l.filter((_, j) => j !== i)) : undefined}
            />
          ))}
          <FieldError>{errori.misure}</FieldError>
          <div>
            <Button
              type="button"
              variant="outline"
              disabled={form.misure.length >= MASSIMO_MISURE}
              onClick={() => misure((l) => [...l, nuovaMisura()])}
            >
              <PlusIcon data-icon="inline-start" />
              Nuova misura
            </Button>
          </div>
        </div>
      </div>

      {erroreServer && (
        <Alert variant="destructive">
          <OctagonXIcon />
          <AlertTitle>Salvataggio non riuscito</AlertTitle>
          <AlertDescription>{erroreServer}</AlertDescription>
        </Alert>
      )}

      <DialogFooter className="sm:items-center">
        {qualcheErrore && !erroreServer && (
          <FieldError className="sm:mr-auto">Controlla i campi segnati.</FieldError>
        )}
        <Button type="button" variant="outline" onClick={onChiudi}>
          Annulla
        </Button>
        <PulsanteInvio type="submit" inCorso={inCorso}>
          Salva
        </PulsanteInvio>
      </DialogFooter>
    </form>
  );
}

/**
 * Una misura del test. Con più misure ha in testa il numero e il menu «⋯» per
 * spostarla ed eliminarla; da sola no.
 */
function CardMisura({
  misura: m,
  nome,
  indice,
  ultima,
  attributi,
  altrove,
  errori,
  onCambia,
  onSposta,
  onElimina,
}: {
  misura: FormMisura;
  nome: string;
  /** La posizione, o `null` se è l'unica misura. */
  indice: number | null;
  ultima: boolean;
  attributi: AttributoDelTest[];
  altrove: ReadonlyMap<string, string>;
  errori: Record<string, string>;
  onCambia: (m: Partial<FormMisura>) => void;
  onSposta: (delta: number) => void;
  onElimina?: () => void;
}) {
  const [prova, setProva] = useState<Record<string, string>>({});
  const errore = (c: string) => errori[`misure.${indice ?? 0}.${c}`];
  const erroreParametro = (c: string) => errore(`parametri.${c}`);
  const p = m.parametri;
  const parametri = (c: Partial<FormParametri>) => onCambia({ parametri: { ...p, ...c } });
  const unitaMisura = m.unita.trim() || null;
  const punteggio = anteprima(p, prova);
  const id = (c: string) => `${m.chiave}-${c}`;
  const alterna = (attributo: string, acceso: boolean) =>
    onCambia({
      attributi: acceso ? [...m.attributi, attributo] : m.attributi.filter((x) => x !== attributo),
    });

  return (
    <Card className="gap-4 py-4">
      <CardContent className="flex flex-col gap-6 px-4">
        {indice !== null && (
          <div className="-mb-2 flex items-center gap-2">
            <span className="flex-1 text-sm font-medium text-muted-foreground">
              Misura {indice + 1}
            </span>
            <MenuAzioni
              riga
              etichetta={`Azioni della misura ${indice + 1}`}
              voci={[
                indice > 0 && {
                  etichetta: 'Sposta su',
                  Icona: ArrowUpIcon,
                  onSelect: () => onSposta(-1),
                },
                !ultima && {
                  etichetta: 'Sposta giù',
                  Icona: ArrowDownIcon,
                  onSelect: () => onSposta(1),
                },
                onElimina && {
                  etichetta: 'Elimina',
                  Icona: Trash2Icon,
                  onSelect: onElimina,
                  distruttiva: true,
                },
              ]}
            />
          </div>
        )}

        <div className="flex flex-col gap-5 sm:flex-row">
          <Field data-invalid={Boolean(errore('nome'))} className="sm:flex-1">
            <FieldLabel htmlFor={id('nome')}>Nome della misura</FieldLabel>
            <Input
              id={id('nome')}
              value={nome}
              placeholder="Parziale 10 m"
              aria-invalid={Boolean(errore('nome'))}
              onChange={(e) => onCambia({ nome: e.target.value, nomeToccato: true })}
            />
            <FieldError>{errore('nome')}</FieldError>
          </Field>
          <Field data-invalid={Boolean(errore('unita'))} className="sm:w-40">
            <FieldLabel htmlFor={id('unita')}>Unità del risultato</FieldLabel>
            <Input
              id={id('unita')}
              value={m.unita}
              placeholder="s, cm, kg, %"
              aria-invalid={Boolean(errore('unita'))}
              onChange={(e) => onCambia({ unita: e.target.value })}
            />
            <FieldError>{errore('unita')}</FieldError>
          </Field>
        </div>

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <h4 className="text-sm font-medium">Attributi valutati</h4>
            <FieldDescription>Il punteggio della misura va uguale a ciascuno.</FieldDescription>
          </div>
          {FAMIGLIE_ATTRIBUTO.map((famiglia) => {
            const voci = attributi.filter((a) => a.famiglia === famiglia);
            if (voci.length === 0) return null;
            const quanti = voci.filter((a) => m.attributi.includes(a.id)).length;
            return (
              // Il riquadro sta fuori dal fieldset, che taglierebbe il bordo con la legenda.
              <div key={famiglia} className="rounded-lg border border-border p-3">
                <FieldSet>
                  <FieldLegend className="flex w-full items-center gap-2">
                    <IconaFamiglia famiglia={famiglia} className="size-4" />
                    <span className="flex-1">{ETICHETTA_FAMIGLIA_ATTRIBUTO[famiglia]}</span>
                    {quanti > 0 && (
                      <span className="text-muted-foreground tabular-nums">{quanti}</span>
                    )}
                  </FieldLegend>
                  <div className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
                    {voci.map((a) => {
                      const scelto = m.attributi.includes(a.id);
                      const altra = altrove.get(a.id);
                      return (
                        <label
                          key={a.id}
                          className={cn(
                            'flex items-center gap-2 text-sm',
                            altra && 'cursor-not-allowed',
                          )}
                        >
                          <Checkbox
                            checked={scelto}
                            disabled={Boolean(altra)}
                            onCheckedChange={(v) => alterna(a.id, v === true)}
                          />
                          <span className={cn(!scelto && 'text-muted-foreground')}>{a.nome}</span>
                          {altra ? (
                            <span className="truncate text-muted-foreground">(in {altra})</span>
                          ) : (
                            !a.attivo && (
                              <span className="text-muted-foreground">(disattivato)</span>
                            )
                          )}
                        </label>
                      );
                    })}
                  </div>
                </FieldSet>
              </div>
            );
          })}
          <FieldError>{errore('attributi')}</FieldError>
        </div>

        <FieldGroup>
          <Field>
            <FieldLabel htmlFor={id('metodo')}>Metodo di calcolo</FieldLabel>
            <Select value={p.metodo} onValueChange={(v) => parametri({ metodo: v as MetodoTest })}>
              <SelectTrigger id={id('metodo')} className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {METODI_TEST.map((metodo) => (
                  <SelectItem key={metodo} value={metodo}>
                    <IconaMetodo metodo={metodo} />
                    {ETICHETTA_METODO_TEST[metodo]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FieldDescription>{DESCRIZIONE_METODO[p.metodo]}</FieldDescription>
          </Field>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {CAMPI_PARAMETRI[p.metodo].map((c) => (
              <CampoNumero
                key={`${p.metodo}-${c.chiave}`}
                id={id(c.chiave)}
                etichetta={c.etichetta}
                unita={unitaDelCampo(c, unitaMisura)}
                valore={p.valori[c.chiave] ?? ''}
                onCambia={(v) => parametri({ valori: { ...p.valori, [c.chiave]: v } })}
                errore={erroreParametro(c.chiave)}
              />
            ))}
          </div>

          {p.metodo === 'gradini' && (
            <EditorNavette
              prefisso={m.chiave}
              navette={p.navette}
              onCambia={(n) => parametri({ navette: { ...p.navette, ...n } })}
              errore={erroreParametro('navettePerLivello')}
            />
          )}
        </FieldGroup>

        {/* Il riquadro sta fuori dal fieldset, che taglierebbe il bordo con la legenda. */}
        <div className="rounded-lg border border-border p-3">
          <FieldSet>
            <FieldLegend>Prova il calcolo</FieldLegend>
            <FieldDescription>Un risultato di esempio: non si salva.</FieldDescription>
            <div className="flex flex-wrap items-end gap-5">
              <div className="grid flex-1 grid-cols-1 gap-5 sm:grid-cols-2">
                {CAMPI_RISULTATO[p.metodo].map((c) => (
                  <CampoNumero
                    key={`${p.metodo}-prova-${c.chiave}`}
                    id={id(`prova-${c.chiave}`)}
                    etichetta={c.etichetta}
                    unita={unitaDelCampo(c, unitaMisura)}
                    valore={prova[c.chiave] ?? ''}
                    onCambia={(v) => setProva((x) => ({ ...x, [c.chiave]: v }))}
                  />
                ))}
              </div>
              <div
                className="flex min-w-24 flex-col items-center rounded-md border border-border bg-background px-3 py-1.5"
                aria-live="polite"
              >
                <span className="text-xs text-muted-foreground">Punteggio</span>
                <span className="text-2xl font-semibold tabular-nums">
                  {punteggio?.punteggio ?? '—'}
                </span>
                {punteggio?.derivato !== undefined && (
                  <span className="text-xs text-muted-foreground tabular-nums">
                    {punteggio.derivato.toLocaleString('it-IT')}{' '}
                    {p.metodo === 'gradini' ? 'm' : 's netti'}
                  </span>
                )}
              </div>
            </div>
          </FieldSet>
        </div>
      </CardContent>
    </Card>
  );
}

function EditorNavette({
  prefisso,
  navette,
  onCambia,
  errore,
}: {
  /** Distingue gli id dei campi quando le misure a gradini sono più d'una. */
  prefisso: string;
  navette: FormParametri['navette'];
  onCambia: (n: Partial<FormParametri['navette']>) => void;
  errore?: string;
}) {
  const cambiaLivello = (i: number, v: string) =>
    onCambia({ lista: navette.lista.map((x, j) => (j === i ? v : x)) });

  return (
    <FieldSet data-invalid={Boolean(errore)}>
      <FieldLegend variant="label">Navette per livello</FieldLegend>
      <div className="flex flex-wrap items-center gap-3">
        <ToggleGroup
          type="single"
          variant="outline"
          value={navette.perLivello ? 'lista' : 'uguali'}
          onValueChange={(v) => v && onCambia({ perLivello: v === 'lista' })}
        >
          <ToggleGroupItem value="uguali">Uguali per tutti</ToggleGroupItem>
          <ToggleGroupItem value="lista">Una per livello</ToggleGroupItem>
        </ToggleGroup>
        {!navette.perLivello && (
          <Input
            aria-label="Navette per ogni livello"
            inputMode="numeric"
            className="w-24"
            value={navette.uguali}
            onChange={(e) => onCambia({ uguali: e.target.value })}
          />
        )}
      </div>
      {navette.perLivello && (
        <div className="flex flex-col gap-2">
          <FieldDescription>
            Il livello 1 del test è il primo del protocollo, anche se il protocollo lo chiama 5 o
            11.1.
          </FieldDescription>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {navette.lista.map((n, i) => (
              <div key={i} className="flex items-center gap-2">
                <FieldLabel htmlFor={`${prefisso}-navette-${i}`} className="w-20">
                  Livello {i + 1}
                </FieldLabel>
                <Input
                  id={`${prefisso}-navette-${i}`}
                  inputMode="numeric"
                  className="w-20"
                  value={n}
                  onChange={(e) => cambiaLivello(i, e.target.value)}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={`Togli il livello ${i + 1}`}
                  disabled={navette.lista.length === 1}
                  onClick={() => onCambia({ lista: navette.lista.filter((_, j) => j !== i) })}
                >
                  <XIcon />
                </Button>
              </div>
            ))}
          </div>
          <div>
            <Button
              type="button"
              variant="outline"
              onClick={() => onCambia({ lista: [...navette.lista, ''] })}
            >
              <PlusIcon data-icon="inline-start" />
              Aggiungi livello
            </Button>
          </div>
        </div>
      )}
      <FieldError>{errore}</FieldError>
    </FieldSet>
  );
}
